import { useState } from "react";
import { Camera } from "lucide-react";

import { CaptureButtons } from "@/components/media/CaptureButtons";
import { MediaThumb } from "@/components/media/MediaThumb";
import { api } from "@/lib/api-client";
import { captionOf, MediaTooLarge, prepareUpload } from "@/lib/media";
import type { OrderPhoto } from "@/lib/orders";

/**
 * Photos and videos of one room, taken while quoting (site survey, existing
 * floor, references). They live on the saved room, so an unsaved room only
 * shows a hint. Uploads stay possible on locked orders — the site team needs
 * them — and follow the room onto the project when it is created.
 */
export function RoomPhotos({
  orderId,
  roomId,
  photos,
  onChanged,
}: {
  orderId: string | null | undefined;
  roomId: string | undefined;
  photos: OrderPhoto[];
  onChanged: () => void | Promise<void>;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const ready = Boolean(orderId && roomId);

  async function add(files: File[]) {
    if (!orderId || !roomId) return;
    setError(null);
    let done = 0;
    try {
      for (const f of files) {
        const n = files.length > 1 ? ` ${done + 1}/${files.length}` : "";
        setBusy(`Uploading${n}…`);
        const { body, filename } = await prepareUpload(f);
        await api.upload(body, { kind: "order_photo", orderId, orderRoomId: roomId, caption: captionOf(f) }, {
          filename,
          onProgress: (p) => setBusy(`Uploading${n} ${Math.round(p * 100)}%`),
        });
        done += 1;
      }
      await onChanged();
    } catch (e) {
      const why = e instanceof MediaTooLarge ? "Video over 500 MB. Record a shorter clip." : "Upload failed. Try again on a better connection.";
      setError(done ? `Uploaded ${done} of ${files.length}. ${why}` : why);
      if (done) await onChanged();
    } finally {
      setBusy(null);
    }
  }

  async function remove(p: OrderPhoto) {
    if (!window.confirm("Delete this file?")) return;
    setBusy("Deleting…");
    try {
      await api.deleteMedia(p.id);
      await onChanged();
    } catch {
      setError("Could not delete the file.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.03] p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="field-label !mb-0">
          <Camera className="mr-1 inline h-3.5 w-3.5" /> Photos & video {photos.length > 0 && <span className="text-ice/50">· {photos.length}</span>}
        </span>
        {ready ? (
          <CaptureButtons onFiles={add} busyLabel={busy} />
        ) : (
          <span className="text-xs text-ice/50">Save the order to add photos.</span>
        )}
      </div>
      {error && <p className="mt-2 text-xs text-pink">{error}</p>}
      {photos.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {photos.map((p) => (
            <MediaThumb key={p.id} url={p.url} mimeType={p.mimeType} caption={p.caption} disabled={Boolean(busy)} onDelete={() => void remove(p)} />
          ))}
        </div>
      )}
    </div>
  );
}
